import PageHeader from 'App/components/PageHeader'
import React, { useCallback, useMemo, useState } from 'react'
import { RouteComponentProps } from 'react-router'
import { useEventsPageState } from 'Event/hooks/useEventsPageState'
import { useGetEvent } from 'Event/hooks/useGetEvent'
import Loader from 'Event/components/loader'
import { OpportunityResponseWithRecurrenceInfo } from 'Event/interfaces/interfaceCreateEditEvent'
import useEventDrilldownStyles from 'Event/hooks/useEventDrilldownStyles'
import { EventsDrilldownInfo } from 'Event/components/EventDrilldownInfo'
import { EventDrilldownRegistrations } from 'Event/components/EventDrilldownRegistrations'
import { EventDrilldownActivities } from 'Event/components/EventDrilldownActivities'
import { useHandleScroll } from 'civic-champs-shared/helpers/useHandleScroll'
import { Summary } from '../components/SummaryTable'
import { RegistrationResponse } from '../scenes/events/detail/volunteers/hooks/RemoveShiftsPrompt'
import { useCurrentOrg } from 'auth/hooks'
import { getEventsPageFallbackUrl } from 'core/navigation/eventsBack'
import { useStackBack } from 'core/navigation/useStackBack'
import { useHistory } from 'react-router'
import { OpportunityResponse } from 'Event/interfaces/interfaceCreateEditEvent'
import { encodeOccurrenceFromEvent } from 'Event/helpers/encodeOccurrence'
import { useEventDrilldownSupplementaryData } from 'Event/hooks/useEventDrilldownSupplementaryData'
import { useEventDrilldownEdit } from 'Event/hooks/useEventDrilldownEdit'
import { EventDrilldownDetails } from 'Event/components/EventDrilldownDetails'
import { useBreakPoint } from 'civic-champs-shared/helpers/useBreakpoint'
import { useFeatureEnabled } from 'core/feature/hooks'

const breakPoint = 768

const useEventDrilldownSummary = (event: OpportunityResponseWithRecurrenceInfo) => {
  const [summary, setSummary] = useState<Summary>(
    () =>
      event.timeshifts?.reduce((acc, shift) => {
        for (const role of shift.roles) {
          if (!acc[role.id]) {
            acc[role.id] = {
              title: role.name,
              registered: 0,
              total: 0,
            }
          }
          acc[role.id].total += role.all
          acc[role.id].registered += role.all - role.available
        }
        return acc
      }, {} as Summary) || {},
  )

  const onAdd = useCallback(
    (result: RegistrationResponse) =>
      result.details.forEach(({ role: { id } }) => {
        setSummary(prev => ({
          ...prev,
          [id]: {
            ...prev[id],
            registered: prev[id].registered + 1,
          },
        }))
      }),
    [],
  )

  return { summary, onAdd }
}

const EventsDrilldownDetailsEnabled = ({
  event,
  encodedOccurrence,
  refresh,
}: {
  event: OpportunityResponseWithRecurrenceInfo
  encodedOccurrence: string
  refresh?: () => void
}) => {
  const classes = useEventDrilldownStyles()
  const history = useHistory()
  const org = useCurrentOrg()
  const isSmall = useBreakPoint(breakPoint)
  const [scrollTop, setScrollTop] = React.useState(0)
  const handleScroll = useHandleScroll(setScrollTop)
  const {
    personGroups,
    eventGroups,
    questionSets,
    loading: supplementaryLoading,
  } = useEventDrilldownSupplementaryData(event)
  const { summary, onAdd } = useEventDrilldownSummary(event)

  const onSuccess = useCallback(
    (updatedEvent: OpportunityResponse) => {
      const url = updatedEvent.schedulable
        ? `/events/${encodeOccurrenceFromEvent(updatedEvent)}/${org.id}`
        : `/opportunities/${encodeOccurrenceFromEvent(updatedEvent)}`
      if (history.location.pathname === url) {
        refresh?.()
      } else {
        history.push(url)
      }
    },
    [history, org.id, refresh],
  )

  const { onEditEventInformation, onEditRegistrationDetails, openLegacyEdit } = useEventDrilldownEdit({
    event,
    encodedOccurrence,
    personGroups,
    onSuccess,
  })

  if (supplementaryLoading) {
    return (
      <div className={classes.root}>
        <Loader />
      </div>
    )
  }

  return (
    <div className={classes.root} onScroll={handleScroll}>
      <EventsDrilldownInfo
        event={event}
        encodedOccurrence={encodedOccurrence}
        scrollTop={scrollTop}
        refresh={refresh}
        summary={summary}
        personGroups={personGroups}
        eventGroups={eventGroups}
        questionSets={questionSets}
        onEditEventInformation={onEditEventInformation}
        openLegacyEdit={openLegacyEdit}
        showSectionEdit={!isSmall}
      />
      {event.schedulable && (
        <EventDrilldownDetails
          event={event}
          encodedOccurrence={encodedOccurrence}
          scrollTop={scrollTop}
          groups={eventGroups}
          questionSets={questionSets}
          onEditEventInformation={onEditEventInformation}
          onEditRegistrationDetails={onEditRegistrationDetails}
          showEditActions={!isSmall}
        />
      )}
      <EventDrilldownRegistrations event={event} scrollTop={scrollTop} onAdd={onAdd} />
      <EventDrilldownActivities event={event} scrollTop={scrollTop} />
    </div>
  )
}

const EventsDrilldownComponent = ({
  event,
  encodedOccurrence,
  refresh,
}: {
  event: OpportunityResponseWithRecurrenceInfo
  encodedOccurrence: string
  refresh?: () => void
}) => {
  const classes = useEventDrilldownStyles()
  const isEventDrilldownDetailsEnabled = useFeatureEnabled('EventDrilldownDetailsSection')
  const [scrollTop, setScrollTop] = React.useState(0)
  const handleScroll = useHandleScroll(setScrollTop)
  const { summary, onAdd } = useEventDrilldownSummary(event)

  if (isEventDrilldownDetailsEnabled) {
    return <EventsDrilldownDetailsEnabled event={event} encodedOccurrence={encodedOccurrence} refresh={refresh} />
  }

  return (
    <div className={classes.root} onScroll={handleScroll}>
      <EventsDrilldownInfo
        event={event}
        encodedOccurrence={encodedOccurrence}
        scrollTop={scrollTop}
        refresh={refresh}
        summary={summary}
      />
      <EventDrilldownRegistrations event={event} scrollTop={scrollTop} onAdd={onAdd} />
      <EventDrilldownActivities event={event} scrollTop={scrollTop} />
    </div>
  )
}

export const EventsDrilldown = (props: RouteComponentProps<{ encodedOccurrence: string }>) => {
  const [eventsPage] = useEventsPageState()
  const org = useCurrentOrg()
  const { encodedOccurrence } = props.match.params
  const { event, loading, refresh } = useGetEvent(encodedOccurrence)

  const fallbackUrl = useMemo(() => getEventsPageFallbackUrl(eventsPage, org.timeZone), [eventsPage, org.timeZone])
  const { label: backLabel, goBack } = useStackBack({ url: fallbackUrl, label: 'Back to Events' })

  if (loading) {
    return <Loader />
  }
  return (
    <PageHeader
      title={(event as OpportunityResponseWithRecurrenceInfo).name}
      subTitle={backLabel}
      onBackClick={goBack}
      noPadding={true}
    >
      <EventsDrilldownComponent
        event={event as OpportunityResponseWithRecurrenceInfo}
        encodedOccurrence={encodedOccurrence}
        refresh={refresh}
      />
    </PageHeader>
  )
}

export default EventsDrilldown
